import React from 'react';
import { Phone, FileText, Building2, Shield, ExternalLink } from 'lucide-react';
export function InterstateOperatingAuthorityPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2938">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2939">
        <div className="container mx-auto px-4" data-id="element-2940">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2941">
            Operating Authority
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2942">
            Interstate Commerce Requirements & Regulations
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2943">
        <div className="max-w-4xl mx-auto" data-id="element-2944">
          {/* Key Requirement */}
          <div className="bg-blue-50 border-2 border-blue-500 rounded-lg shadow-md p-8 mb-8" data-id="element-2945">
            <div className="flex items-start mb-4" data-id="element-2946">
              <Building2 className="text-blue-600 mr-4 flex-shrink-0 mt-1" size={32} data-id="element-2947" />
              <div data-id="element-2948">
                <h2 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-2949">
                  Who Needs Operating Authority?
                </h2>
                <p className="text-lg text-gray-800 italic" data-id="element-2950">
                  <strong data-id="element-2951">
                    Companies that operate as "for hire" carriers (for a fee or
                    other compensation) that transport passengers or federally
                    regulated commodities, or arrange for their transport, in
                    interstate commerce are also required to have interstate
                    operating authority.
                  </strong>
                </p>
              </div>
            </div>
          </div>

          {/* The Reach of Operating Authority */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2952">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2953">
              The Reach of Operating Authority
            </h2>

            <div className="space-y-6" data-id="element-2954">
              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-2955">
                <p className="text-gray-700 mb-4" data-id="element-2956">
                  FMCSA operating authority is also referred to as an{' '}
                  <strong data-id="element-2957">"MC," "FF," or "MX" number</strong>, depending on the
                  type of authority that is granted.
                </p>
                <p className="text-gray-700" data-id="element-2958">
                  Unlike the USDOT Number application process, a company may
                  need to obtain <strong data-id="element-2959">multiple operating authorities</strong>{' '}
                  to support its planned business operations.
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2960">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-2961">
                  Operating Authority Dictates:
                </h3>
                <div className="grid md:grid-cols-3 gap-4" data-id="element-2962">
                  <div className="bg-white rounded-lg p-4" data-id="element-2963">
                    <p className="font-semibold text-blue-900 mb-2" data-id="element-2964">
                      Type of Operation
                    </p>
                    <p className="text-sm text-gray-700" data-id="element-2965">
                      What kind of business operations a company may run
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4" data-id="element-2966">
                    <p className="font-semibold text-green-900 mb-2" data-id="element-2967">
                      Cargo Type
                    </p>
                    <p className="text-sm text-gray-700" data-id="element-2968">
                      What cargo the company may carry
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4" data-id="element-2969">
                    <p className="font-semibold text-purple-900 mb-2" data-id="element-2970">
                      Geographic Area
                    </p>
                    <p className="text-sm text-gray-700" data-id="element-2971">
                      Where the company may legally operate
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Responsibility and Exemptions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2972">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2973">
              <Shield className="mr-3 text-[#B1050B]" size={28} data-id="element-2974" />
              Financial Responsibility and Exemptions
            </h2>

            <div className="space-y-6" data-id="element-2975">
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2976">
                <p className="text-gray-700" data-id="element-2977">
                  All of this also dictates the level of{' '}
                  <strong data-id="element-2978">insurance/financial responsibilities</strong> a
                  company must maintain.
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-2979">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-2980">
                  Carriers NOT Required to Have Operating Authority:
                </h3>

                <div className="space-y-3" data-id="element-2981">
                  <div className="bg-white rounded-lg p-4 flex items-start" data-id="element-2982">
                    <span className="text-green-600 mr-3 flex-shrink-0 text-xl" data-id="element-2983">
                      ✓
                    </span>
                    <div data-id="element-2984">
                      <p className="font-semibold text-gray-900" data-id="element-2985">
                        Private Carriers
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2986">
                        Companies transporting their own goods
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 flex items-start" data-id="element-2987">
                    <span className="text-green-600 mr-3 flex-shrink-0 text-xl" data-id="element-2988">
                      ✓
                    </span>
                    <div data-id="element-2989">
                      <p className="font-semibold text-gray-900" data-id="element-2990">
                        "For-Hire" Carriers with Exempt Commodities
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2991">
                        Exclusively hauling cargo that is not federally
                        regulated
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 flex items-start" data-id="element-2992">
                    <span className="text-green-600 mr-3 flex-shrink-0 text-xl" data-id="element-2993">
                      ✓
                    </span>
                    <div data-id="element-2994">
                      <p className="font-semibold text-gray-900" data-id="element-2995">
                        Commercial Zone Operators
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2996">
                        Operating exclusively within a federally designated
                        "commercial zone" exempt from interstate authority rules
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2997">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2998">
                  What is a Commercial Zone?
                </h3>
                <p className="text-gray-700" data-id="element-2999">
                  A commercial zone is a geographic territory that includes
                  multiple states bordering on a major metropolitan city.
                </p>
                <p className="text-sm text-gray-600 mt-2 italic" data-id="element-3000">
                  Example: Virginia/Maryland/Washington, DC area
                </p>
              </div>
            </div>
          </div>

          {/* Part 365 Regulations */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3001">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3002">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-3003" />
              Part 365: Rules Governing Applications for Operating Authority
            </h2>

            <div className="space-y-6" data-id="element-3004">
              {/* Subpart A */}
              <div data-id="element-3005">
                <h3 className="text-xl font-bold text-blue-900 mb-4 bg-blue-50 p-3 rounded-lg" data-id="element-3006">
                  Subpart A - How to Apply for Operating Authority
                </h3>
                <div className="space-y-2" data-id="element-3007">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.101&section_toc=1039" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3008">
                    <div className="flex items-center" data-id="element-3009">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3010">
                        365.101
                      </span>
                      <span className="text-gray-900" data-id="element-3011">
                        Applications governed by these rules
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3012" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.103&section_toc=1040" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3013">
                    <div className="flex items-center" data-id="element-3014">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3015">
                        365.103
                      </span>
                      <span className="text-gray-900" data-id="element-3016">Modified procedure</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3017" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.105&section_toc=1041" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3018">
                    <div className="flex items-center" data-id="element-3019">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3020">
                        365.105
                      </span>
                      <span className="text-gray-900" data-id="element-3021">
                        Starting the application process: Form OP-1
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3022" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.107&section_toc=1042" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3023">
                    <div className="flex items-center" data-id="element-3024">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3025">
                        365.107
                      </span>
                      <span className="text-gray-900" data-id="element-3026">
                        Types of applications
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3027" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.109&section_toc=1043" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3028">
                    <div className="flex items-center" data-id="element-3029">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3030">
                        365.109
                      </span>
                      <span className="text-gray-900" data-id="element-3031">
                        Commission review of the application
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3032" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.111&section_toc=1044" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3033">
                    <div className="flex items-center" data-id="element-3034">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3035">
                        365.111
                      </span>
                      <span className="text-gray-900" data-id="element-3036">
                        Appeals to rejections of the application
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3037" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.113&section_toc=1045" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3038">
                    <div className="flex items-center" data-id="element-3039">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3040">
                        365.113
                      </span>
                      <span className="text-gray-900" data-id="element-3041">
                        Changing the request for authority or filing
                        supplementary evidence
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3042" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.115&section_toc=1046" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3043">
                    <div className="flex items-center" data-id="element-3044">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3045">
                        365.115
                      </span>
                      <span className="text-gray-900" data-id="element-3046">
                        After publication in the FMCSA Register
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3047" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.117&section_toc=1047" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3048">
                    <div className="flex items-center" data-id="element-3049">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3050">
                        365.117
                      </span>
                      <span className="text-gray-900" data-id="element-3051">
                        Obtaining a copy of the application
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3052" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.119&section_toc=1048" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3053">
                    <div className="flex items-center" data-id="element-3054">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3055">
                        365.119
                      </span>
                      <span className="text-gray-900" data-id="element-3056">
                        Opposed applications
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3057" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.121&section_toc=1049" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3058">
                    <div className="flex items-center" data-id="element-3059">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3060">
                        365.121
                      </span>
                      <span className="text-gray-900" data-id="element-3061">
                        Filing a reply statement
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3062" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.123&section_toc=1050" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3063">
                    <div className="flex items-center" data-id="element-3064">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3065">
                        365.123
                      </span>
                      <span className="text-gray-900" data-id="element-3066">
                        Applicant withdrawal
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3067" />
                  </a>
                </div>
              </div>

              {/* Subpart B */}
              <div data-id="element-3068">
                <h3 className="text-xl font-bold text-green-900 mb-4 bg-green-50 p-3 rounded-lg" data-id="element-3069">
                  Subpart B - How to Oppose Requests for Authority
                </h3>
                <div className="space-y-2" data-id="element-3070">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.201&section_toc=1052" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3071">
                    <div className="flex items-center" data-id="element-3072">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3073">
                        365.201
                      </span>
                      <span className="text-gray-900" data-id="element-3074">Definitions</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3075" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.203&section_toc=1053" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3076">
                    <div className="flex items-center" data-id="element-3077">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3078">
                        365.203
                      </span>
                      <span className="text-gray-900" data-id="element-3079">Time for filing</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3080" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.205&section_toc=1054" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3081">
                    <div className="flex items-center" data-id="element-3082">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3083">
                        365.205
                      </span>
                      <span className="text-gray-900" data-id="element-3084">
                        Contents of the protest
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3085" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.207&section_toc=1055" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3086">
                    <div className="flex items-center" data-id="element-3087">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3088">
                        365.207
                      </span>
                      <span className="text-gray-900" data-id="element-3089">Withdrawal</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3090" />
                  </a>
                </div>
              </div>

              {/* Subpart C */}
              <div data-id="element-3091">
                <h3 className="text-xl font-bold text-purple-900 mb-4 bg-purple-50 p-3 rounded-lg" data-id="element-3092">
                  Subpart C - General Rules Governing the Application Process
                </h3>
                <div className="space-y-2" data-id="element-3093">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.301&section_toc=1057" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3094">
                    <div className="flex items-center" data-id="element-3095">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3096">
                        365.301
                      </span>
                      <span className="text-gray-900" data-id="element-3097">Applicable rules</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3098" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.303&section_toc=1058" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3099">
                    <div className="flex items-center" data-id="element-3100">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3101">
                        365.303
                      </span>
                      <span className="text-gray-900" data-id="element-3102">
                        Contacting another party
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3103" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.305&section_toc=1059" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3104">
                    <div className="flex items-center" data-id="element-3105">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3106">
                        365.305
                      </span>
                      <span className="text-gray-900" data-id="element-3107">
                        Serving copies of pleadings
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3108" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.307&section_toc=1060" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3109">
                    <div className="flex items-center" data-id="element-3110">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3111">
                        365.307
                      </span>
                      <span className="text-gray-900" data-id="element-3112">Replies to motions</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3113" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.309&section_toc=1061" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3114">
                    <div className="flex items-center" data-id="element-3115">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3116">
                        365.309
                      </span>
                      <span className="text-gray-900" data-id="element-3117">FAX filings</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3118" />
                  </a>
                </div>
              </div>

              {/* Subpart D */}
              <div data-id="element-3119">
                <h3 className="text-xl font-bold text-orange-900 mb-4 bg-orange-50 p-3 rounded-lg" data-id="element-3120">
                  Subpart D - Transfer of Operating Rights Under 49 U.S.C. 10926
                </h3>
                <div className="space-y-2" data-id="element-3121">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.401&section_toc=1063" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3122">
                    <div className="flex items-center" data-id="element-3123">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3124">
                        365.401
                      </span>
                      <span className="text-gray-900" data-id="element-3125">Scope of rules</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3126" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.403&section_toc=1064" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3127">
                    <div className="flex items-center" data-id="element-3128">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3129">
                        365.403
                      </span>
                      <span className="text-gray-900" data-id="element-3130">Definitions</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3131" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.405&section_toc=1065" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3132">
                    <div className="flex items-center" data-id="element-3133">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3134">
                        365.405
                      </span>
                      <span className="text-gray-900" data-id="element-3135">Applications</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3136" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.407&section_toc=1066" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3137">
                    <div className="flex items-center" data-id="element-3138">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3139">
                        365.407
                      </span>
                      <span className="text-gray-900" data-id="element-3140">Notice</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3141" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.409&section_toc=1067" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3142">
                    <div className="flex items-center" data-id="element-3143">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3144">
                        365.409
                      </span>
                      <span className="text-gray-900" data-id="element-3145">
                        Commission action and criteria for approval
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3146" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.411&section_toc=1068" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3147">
                    <div className="flex items-center" data-id="element-3148">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3149">
                        365.411
                      </span>
                      <span className="text-gray-900" data-id="element-3150">
                        Responsive pleadings
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3151" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.413&section_toc=1069" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3152">
                    <div className="flex items-center" data-id="element-3153">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3154">
                        365.413
                      </span>
                      <span className="text-gray-900" data-id="element-3155">
                        Procedures for changing name or business form
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3156" />
                  </a>
                </div>
              </div>

              {/* Subpart E */}
              <div data-id="element-3157">
                <h3 className="text-xl font-bold text-red-900 mb-4 bg-red-50 p-3 rounded-lg" data-id="element-3158">
                  Subpart E - Special Rules for Certain Mexico-domiciled
                  Carriers
                </h3>
                <div className="space-y-2" data-id="element-3159">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.501&section_toc=118438" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3160">
                    <div className="flex items-center" data-id="element-3161">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3162">
                        365.501
                      </span>
                      <span className="text-gray-900" data-id="element-3163">Scope of rules</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3164" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.503&section_toc=118439" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3165">
                    <div className="flex items-center" data-id="element-3166">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3167">
                        365.503
                      </span>
                      <span className="text-gray-900" data-id="element-3168">Application</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3169" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.505&section_toc=118440" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3170">
                    <div className="flex items-center" data-id="element-3171">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3172">
                        365.505
                      </span>
                      <span className="text-gray-900" data-id="element-3173">
                        Re-registration and fee waiver for certain applicants
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3174" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.507&section_toc=118441" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3175">
                    <div className="flex items-center" data-id="element-3176">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3177">
                        365.507
                      </span>
                      <span className="text-gray-900" data-id="element-3178">
                        FMCSA action on the application
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3179" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.509&section_toc=118442" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3180">
                    <div className="flex items-center" data-id="element-3181">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3182">
                        365.509
                      </span>
                      <span className="text-gray-900" data-id="element-3183">
                        Requirement to notify FMCSA of change in applicant
                        information
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3184" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.511&section_toc=118443" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-3185">
                    <div className="flex items-center" data-id="element-3186">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-3187">
                        365.511
                      </span>
                      <span className="text-gray-900" data-id="element-3188">
                        Requirement for CVSA inspection during first 3 years
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-3189" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-3190">
            <h2 className="text-3xl font-bold mb-4" data-id="element-3191">
              Need Help with Operating Authority?
            </h2>
            <p className="text-xl mb-6" data-id="element-3192">
              Expert guidance on interstate commerce requirements and FMCSA
              compliance
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-3193">
              <Phone className="mr-3" size={24} data-id="element-3194" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-3195">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}